import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { mensagemErro } from '../api/axios';
import {
  confirmarArquivos,
  excluirArquivo,
  previaExclusaoArquivo,
  visaoArquivos,
} from '../api/fluxfin';
import ImpactoDoEnvio from '../components/ImpactoDoEnvio';
import {
  Ajuda,
  Alerta,
  Botao,
  Busca,
  LoadingSpinner,
  PageHeader,
  Paginacao,
  Painel,
  StatusArquivoBadge,
  Tabela,
  Th,
  Vazio,
} from '../components/common';
import { useAuth } from '../contexts/AuthContext';
import { useOrdenacao } from '../hooks/useOrdenacao';
import { usePaginacao } from '../hooks/usePaginacao';
import { brl, formatDateTime, numero } from '../utils/formato';

/* ── Confirmar a exclusão de um recebimento ────────────────────────────
 *
 * Apagar um arquivo não é operação de rotina: desfaz uma conciliação inteira
 * e muda o valor a baixar daquele protocolo. Por isso a tela pergunta com
 * números, e não com um "tem certeza?" genérico — e diz também **o que fica**,
 * porque é a parte preservada que torna a decisão reversível na prática.
 */
function ConfirmarExclusaoArquivo({ arquivo, aoFechar, aoExcluir }) {
  const [previa, setPrevia] = useState(null);
  const [erro, setErro] = useState('');
  const [agindo, setAgindo] = useState(false);
  const [limparResolucoes, setLimparResolucoes] = useState(false);

  useEffect(() => {
    previaExclusaoArquivo(arquivo.id_arquivo)
      .then((r) => setPrevia(r.data))
      .catch((e) => setErro(mensagemErro(e, 'Não foi possível ler o arquivo.')));
  }, [arquivo.id_arquivo]);

  async function confirmar() {
    setAgindo(true);
    setErro('');
    try {
      await excluirArquivo(arquivo.id_arquivo, { limparResolucoes });
      aoExcluir(`"${arquivo.nome_original}" foi excluído.`);
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível excluir.'));
      setAgindo(false);
    }
  }

  const sai = previa?.sai || {};
  const itens = [
    ['guias', 'guias'],
    ['linhas', 'linhas de procedimento'],
    ['glosas', 'itens de glosa'],
    ['divergencias', 'divergências'],
    ['pendencias', 'pendências'],
  ].filter(([k]) => sai[k] > 0);

  return (
    <>
      <div className="modal-backdrop fade show" style={{ background: 'rgba(16,24,40,.5)' }} />
      <div className="modal fade show d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div
            className="modal-content"
            style={{ borderRadius: 16, border: '1px solid var(--ff-border)' }}
          >
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <div>
                <h2 className="ff-painel__titulo">Excluir recebimento</h2>
                <p className="ff-painel__sub">
                  {arquivo.nome_original}
                  {arquivo.competencia ? ` · competência ${arquivo.competencia}` : ''}
                </p>
              </div>
              <button type="button" className="btn-close" onClick={aoFechar} aria-label="Fechar" />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>

              {!previa ? (
                <LoadingSpinner />
              ) : (
                <>
                  <div className="ff-aviso ff-aviso--atencao" style={{ margin: 0 }}>
                    <i className="bi bi-exclamation-triangle" aria-hidden="true" />
                    <div>
                      <strong>Isto desfaz a conciliação deste arquivo.</strong> O
                      valor a baixar dos protocolos que ele alimentava muda junto.
                      Não há como desfazer.
                    </div>
                  </div>

                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      Vai ser apagado
                    </div>
                    {itens.length === 0 ? (
                      <p style={{ fontSize: 13, color: 'var(--ff-muted)', margin: 0 }}>
                        Nada foi extraído deste arquivo — só o registro do envio.
                      </p>
                    ) : (
                      <ul style={{ fontSize: 14, marginBottom: 0 }}>
                        {itens.map(([k, rotulo]) => (
                          <li key={k}>
                            <strong>{numero(sai[k])}</strong> {rotulo}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  {/* O que fica é o que torna a decisão reversível na prática:
                      reenviar o arquivo não vai consultar de novo a produção
                      do cliente pelas guias já resolvidas. */}
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                      Vai continuar existindo
                    </div>
                    <ul style={{ fontSize: 14, color: 'var(--ff-muted)', marginBottom: 0 }}>
                      {!limparResolucoes && (
                        <li>
                          <strong>
                            {numero(previa.fica.resolucoes_de_protocolo)}
                          </strong>{' '}
                          vínculo(s) guia → protocolo já resolvidos. Eles não mudam
                          depois de faturado, então reenviar o arquivo{' '}
                          <strong>não</strong> consulta o Bioslab de novo por eles.
                        </li>
                      )}
                      <li>
                        A trilha de auditoria, com os números de agora — é por ela
                        que esta exclusão continua explicável depois.
                      </li>
                    </ul>
                  </div>

                  {previa.fica.resolucoes_de_protocolo > 0 && (
                    <label
                      className="d-flex align-items-start gap-2 p-3"
                      style={{
                        border: '1px solid var(--ff-border-soft)',
                        borderRadius: 10,
                        cursor: 'pointer',
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={limparResolucoes}
                        onChange={(e) => setLimparResolucoes(e.target.checked)}
                        style={{ marginTop: 3 }}
                      />
                      <span style={{ fontSize: 13 }}>
                        <strong>
                          Apagar também os {numero(previa.fica.resolucoes_de_protocolo)}{' '}
                          vínculos guia → protocolo
                        </strong>
                        <span style={{ display: 'block', color: 'var(--ff-muted)' }}>
                          Só marque para <strong>testar o fluxo do zero</strong>: sem
                          isso, reenviar este arquivo reaproveita o que já foi
                          resolvido e não chega a consultar o Bioslab — o que é
                          certo no dia a dia e um falso positivo num teste de
                          conciliação. Marcando, a próxima ingestão consulta a
                          produção do cliente de novo.
                        </span>
                      </span>
                    </label>
                  )}
                </>
              )}
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={agindo}>
                Cancelar
              </Botao>
              <button
                type="button"
                className="ff-btn ff-btn--perigo"
                onClick={confirmar}
                disabled={agindo || !previa}
              >
                {agindo ? 'Excluindo…' : 'Excluir definitivamente'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

/* ── Confirmar vários de uma vez ───────────────────────────────────────
 *
 * O cliente recebe de 6 a 8 demonstrativos por competência, e abrir cada um
 * só para clicar em "Confirmar revisão" era navegação, não conferência.
 *
 * O que NÃO muda: confirmar é o ato que transforma dado extraído em dinheiro
 * oficial — é a regra que o cliente fixou no levantamento. Por isso a seleção
 * não dispara nada sozinha: o diálogo mostra **a soma do que vai passar a
 * contar** antes do clique. O que sai do caminho é o vai-e-volta entre telas,
 * não a conferência.
 */
function DialogoConfirmacao({ arquivos, aoFechar, aoConfirmar }) {
  const [erro, setErro] = useState('');
  const [agindo, setAgindo] = useState(false);

  const total = arquivos.reduce((s, a) => s + Number(a.total_extraido || 0), 0);
  const guias = arquivos.reduce((s, a) => s + Number(a.qtd_guias || 0), 0);

  // Agrupado por operadora × competência: é assim que a pessoa pensa no lote
  // que acabou de subir, e é o recorte em que um arquivo fora do lugar salta
  // aos olhos — uma competência que não era para estar ali.
  const grupos = useMemo(() => {
    const m = new Map();
    arquivos.forEach((a) => {
      const k = `${a.operadora} · ${a.competencia || 'sem competência'}`;
      const g = m.get(k) || { chave: k, qtd: 0, total: 0 };
      g.qtd += 1;
      g.total += Number(a.total_extraido || 0);
      m.set(k, g);
    });
    return [...m.values()].sort((x, y) => x.chave.localeCompare(y.chave));
  }, [arquivos]);

  async function confirmar() {
    setAgindo(true);
    setErro('');
    try {
      const { data } = await confirmarArquivos(arquivos.map((a) => a.id_arquivo));
      aoConfirmar(data);
    } catch (e) {
      setErro(mensagemErro(e, 'Não foi possível confirmar.'));
      setAgindo(false);
    }
  }

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" tabIndex="-1" role="dialog">
        <div className="modal-dialog modal-dialog-centered">
          <div className="ff-modal modal-content">
            <div className="modal-header" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <h5 className="modal-title">
                Confirmar {numero(arquivos.length)} arquivo
                {arquivos.length === 1 ? '' : 's'}
              </h5>
              <button type="button" className="btn-close" onClick={aoFechar} />
            </div>

            <div className="modal-body d-flex flex-column gap-3">
              <Alerta tipo="erro">{erro}</Alerta>

              <p style={{ fontSize: 14, marginBottom: 0 }}>
                A partir daqui estes arquivos <strong>passam a contar</strong> na
                baixa por protocolo, no saldo e nos títulos a lançar. Até agora as
                guias estavam gravadas, mas fora das contas.
              </p>

              <div
                className="p-3 d-flex flex-wrap gap-4"
                style={{
                  border: '1px solid var(--ff-border-soft)',
                  borderRadius: 10,
                }}
              >
                <div>
                  <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                    Bruto pago que passa a contar
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>{brl(total)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12, color: 'var(--ff-muted)' }}>Guias</div>
                  <div style={{ fontWeight: 700, fontSize: 20 }}>{numero(guias)}</div>
                </div>
              </div>

              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6 }}>
                  O que está selecionado
                </div>
                <ul
                  style={{
                    fontSize: 14,
                    color: 'var(--ff-muted)',
                    marginBottom: 0,
                    maxHeight: 180,
                    overflowY: 'auto',
                  }}
                >
                  {grupos.map((g) => (
                    <li key={g.chave}>
                      <strong>{g.chave}</strong> — {numero(g.qtd)} arquivo
                      {g.qtd === 1 ? '' : 's'}, {brl(g.total)}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="modal-footer" style={{ borderColor: 'var(--ff-border-soft)' }}>
              <Botao variante="secundario" onClick={aoFechar} disabled={agindo}>
                Cancelar
              </Botao>
              <Botao variante="confirmar" onClick={confirmar} disabled={agindo}>
                {agindo
                  ? 'Confirmando…'
                  : `Confirmar ${numero(arquivos.length)} arquivo${
                      arquivos.length === 1 ? '' : 's'
                    }`}
              </Botao>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Arquivos() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [aExcluir, setAExcluir] = useState(null);
  const [aviso, setAviso] = useState('');
  const [itens, setItens] = useState([]);
  const [busca, setBusca] = useState('');
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [marcados, setMarcados] = useState(() => new Set());
  const [aConfirmar, setAConfirmar] = useState(null);
  const [confirmados, setConfirmados] = useState([]);

  const carregar = useCallback(
    () =>
      visaoArquivos()
        .then((r) => setItens(r.data))
        .catch((e) =>
          setErro(mensagemErro(e, 'Não foi possível carregar os arquivos.'))
        )
        .finally(() => setCarregando(false)),
    []
  );

  useEffect(() => {
    carregar();
  }, [carregar]);

  const filtrados = useMemo(() => {
    const t = busca.trim().toLowerCase();
    if (!t) return itens;
    return itens.filter(
      (a) =>
        a.operadora?.toLowerCase().includes(t) ||
        a.nome_original?.toLowerCase().includes(t) ||
        a.competencia?.toLowerCase().includes(t)
    );
  }, [itens, busca]);

  /* Confirmável = aguardando revisão **e** com a aritmética fechada.
     Oferecer caixa num arquivo reprovado seria oferecer trabalho que o
     servidor vai recusar — e a recusa está certa: o que não fecha contra os
     totais do próprio documento não pode virar dinheiro oficial. */
  const confirmaveis = useMemo(
    () => filtrados.filter((a) => a.status === 'em_revisao' && a.extracao_aprovada),
    [filtrados]
  );

  /* A seleção acompanha o filtro. Sem isto, buscar "GEAP" depois de marcar
     arquivos da Unimed deixaria a barra dizendo "3 selecionados" sobre linhas
     que não estão na tela — e a confirmação pegaria o que a pessoa não vê. */
  useEffect(() => {
    const visiveis = new Set(confirmaveis.map((a) => a.id_arquivo));
    setMarcados((atual) => {
      const podados = new Set([...atual].filter((id) => visiveis.has(id)));
      return podados.size === atual.size ? atual : podados;
    });
  }, [confirmaveis]);

  const selecionados = useMemo(
    () => confirmaveis.filter((a) => marcados.has(a.id_arquivo)),
    [confirmaveis, marcados]
  );
  const totalSelecionado = selecionados.reduce(
    (s, a) => s + Number(a.total_extraido || 0),
    0
  );
  const todosMarcados =
    confirmaveis.length > 0 && marcados.size === confirmaveis.length;

  const alternar = (id) =>
    setMarcados((atual) => {
      const novo = new Set(atual);
      if (novo.has(id)) novo.delete(id);
      else novo.add(id);
      return novo;
    });

  const marcarTodos = () =>
    setMarcados(new Set(confirmaveis.map((a) => a.id_arquivo)));

  /* O resultado nunca é só "pronto": uma confirmação pode ter três desfechos
     ao mesmo tempo, e colapsá-los num texto só esconderia o que falhou. */
  const aplicarResultado = (r) => {
    setAConfirmar(null);
    setMarcados(new Set());
    setConfirmados(r.confirmados.map((c) => c.id_arquivo));

    const partes = [];
    if (r.confirmados.length) {
      partes.push(
        `${r.confirmados.length} arquivo(s) confirmado(s), somando ${brl(
          r.total_confirmado
        )}.`
      );
    }
    if (r.ja_confirmados.length) {
      partes.push(`${r.ja_confirmados.length} já estava(m) confirmado(s).`);
    }
    if (r.falharam.length) {
      // O motivo vai junto: "falhou" sozinho manda a pessoa abrir um por um
      // para descobrir o que já se sabe aqui.
      setErro(
        `Não foi possível confirmar ${r.falharam.length}: ` +
          r.falharam
            .map((f) => `${f.nome_original || f.id_arquivo} — ${f.motivo}`)
            .join(' · ')
      );
    }
    setAviso(partes.join(' '));
    carregar();
  };

  const ord = useOrdenacao(filtrados, 'created_at', false);
  const pag = usePaginacao(ord.ordenados, 15);

  if (carregando) return <LoadingSpinner />;

  return (
    <>
      <PageHeader
        titulo="Arquivos recebidos"
        subtitulo="Cada demonstrativo, com o resultado das conferências contra o próprio documento."
        acoes={
          <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
            <i className="bi bi-upload" aria-hidden="true" />
            Lançar recebimento
          </Link>
        }
      />

      <Alerta tipo="erro" onFechar={() => setErro('')}>
        {erro}
      </Alerta>
      <Alerta tipo="ok" onFechar={() => setAviso('')}>
        {aviso}
      </Alerta>

      {/* Só aparece quando há o que confirmar. Uma barra permanentemente
          vazia vira moldura e deixa de ser lida. */}
      {confirmaveis.length > 0 && (
        <div className="ff-aviso ff-aviso--info" style={{ alignItems: 'center' }}>
          <i className="bi bi-check2-square" aria-hidden="true" />
          <div className="d-flex flex-wrap align-items-center gap-2" style={{ flex: 1 }}>
            <span>
              {marcados.size === 0 ? (
                <>
                  <strong>{numero(confirmaveis.length)}</strong> arquivo
                  {confirmaveis.length === 1 ? '' : 's'} aguardando revisão
                  {busca.trim() && <> neste recorte</>}.
                </>
              ) : (
                <>
                  <strong>
                    {numero(marcados.size)} de {numero(confirmaveis.length)}
                  </strong>{' '}
                  selecionado{marcados.size === 1 ? '' : 's'} ·{' '}
                  <strong>{brl(totalSelecionado)}</strong>
                </>
              )}
            </span>

            {/* O rótulo diz QUANTOS vai marcar, e "neste recorte" quando há
                busca ativa: "selecionar todos" sem número mente assim que
                existe filtro ou mais de uma página. */}
            {!todosMarcados && (
              <Botao variante="secundario" pequeno onClick={marcarTodos}>
                Selecionar {numero(confirmaveis.length)}
                {busca.trim() ? ' neste recorte' : ''}
              </Botao>
            )}
            {marcados.size > 0 && (
              <Botao
                variante="secundario"
                pequeno
                onClick={() => setMarcados(new Set())}
              >
                Limpar seleção
              </Botao>
            )}
          </div>

          <Botao
            variante="confirmar"
            pequeno
            disabled={marcados.size === 0}
            onClick={() => setAConfirmar(selecionados)}
          >
            <i className="bi bi-check2" aria-hidden="true" />
            Confirmar selecionados
          </Botao>
        </div>
      )}

      {/* A costura com o passo seguinte: confirmado o lote, o trabalho vira
          editar títulos no Nasajon — e a pessoa não deveria ter de descobrir
          sozinha quais. */}
      {confirmados.length > 0 && (
        <ImpactoDoEnvio
          idsArquivo={confirmados}
          titulo="O que esta confirmação deixou para lançar"
          frase="Esta confirmação afeta"
        />
      )}

      <Painel
        titulo="Demonstrativos"
        subtitulo={`${itens.length} arquivo${itens.length === 1 ? '' : 's'} recebido${itens.length === 1 ? '' : 's'}`}
        busca={
          itens.length > 0 && (
            <Busca
              valor={busca}
              onChange={setBusca}
              placeholder="Buscar arquivo ou operadora"
              id="busca-arquivos"
            />
          )
        }
        rodape={filtrados.length > 0 && <Paginacao {...pag} rotulo="arquivos" />}
      >
        {itens.length === 0 ? (
          <Vazio
            icone="bi-file-earmark-text"
            titulo="Nenhum arquivo recebido"
            descricao="Envie o demonstrativo de pagamento de uma operadora para começar a conciliação."
            acao={
              <Link to="/arquivos/novo" className="ff-btn ff-btn--primario">
                Lançar recebimento
              </Link>
            }
          />
        ) : filtrados.length === 0 ? (
          <Vazio
            icone="bi-search"
            titulo="Nenhum arquivo corresponde à busca"
            descricao={`Nada encontrado para "${busca}".`}
          />
        ) : (
          <Tabela>
            <thead>
              <tr>
                {confirmaveis.length > 0 && (
                  <th style={{ width: 36 }}>
                    {/* Marca/desmarca TODOS os confirmáveis do recorte, não só
                        os da página aberta — e por isso o número aparece na
                        barra acima, onde dá para ler antes de clicar. */}
                    <input
                      type="checkbox"
                      checked={todosMarcados}
                      ref={(el) => {
                        if (el) {
                          el.indeterminate =
                            marcados.size > 0 && !todosMarcados;
                        }
                      }}
                      onChange={() =>
                        todosMarcados ? setMarcados(new Set()) : marcarTodos()
                      }
                      aria-label="Selecionar todos os arquivos a confirmar"
                    />
                  </th>
                )}
                <Th campo="operadora" estado={ord}>
                  Operadora
                </Th>
                <Th campo="nome_original" estado={ord}>
                  Arquivo
                </Th>
                <Th campo="competencia" estado={ord}>
                  Competência
                  <Ajuda titulo="Competência" origem="Informada no upload · cabeçalho do demonstrativo">
                    Mês em que a operadora efetuou o pagamento. Não é o mês do atendimento —
                    um único pagamento costuma cobrir atendimentos de vários meses.
                  </Ajuda>
                </Th>
                <Th campo="qtd_guias" estado={ord} alinhar="right">
                  Guias
                </Th>
                <Th campo="total_extraido" estado={ord} alinhar="right">
                  Bruto pago
                  <Ajuda
                    titulo="Bruto pago"
                    origem="Arquivo da operadora · soma de Valor (R$)"
                  >
                    Tudo que a operadora pagou neste arquivo, antes de impostos retidos e
                    descontos. É deste valor que saem as baixas por protocolo.
                  </Ajuda>
                </Th>
                <Th campo="diferenca_bruto_liquido" estado={ord} alinhar="right">
                  Retido/desc.
                  <Ajuda
                    titulo="Retido e descontado"
                    origem="bruto pago − líquido creditado"
                  >
                    Impostos retidos na fonte (PIS/COFINS/CSLL, IRRF, ISS) mais descontos
                    gerais. NÃO é erro de conciliação: existe só no total do arquivo, sem
                    vínculo com guia, e por isso não entra na baixa.
                  </Ajuda>
                </Th>
                <Th campo="extracao_aprovada" estado={ord}>
                  Conferências
                  <Ajuda titulo="Conferências aritméticas">
                    Comparam o que foi extraído com os totais que a própria operadora
                    imprimiu no documento. Três em verde significa que a leitura está
                    provada, não apenas plausível.
                  </Ajuda>
                </Th>
                <Th campo="status" estado={ord}>
                  Status
                </Th>
                <Th campo="created_at" estado={ord} alinhar="right">
                  Recebido em
                </Th>
                {isAdmin && <Th alinhar="right">Administração</Th>}
              </tr>
            </thead>
            <tbody>
              {pag.paginados.map((a) => {
                const conf = a.conferencias || [];
                const falhas = conf.filter((c) => !c.passou).length;
                const podeConfirmar =
                  a.status === 'em_revisao' && a.extracao_aprovada;
                return (
                  <tr
                    key={a.id_arquivo}
                    className="ff-linha-clicavel"
                    onClick={() => navigate(`/arquivos/${a.id_arquivo}`)}
                  >
                    {confirmaveis.length > 0 && (
                      <td onClick={(e) => e.stopPropagation()}>
                        {podeConfirmar ? (
                          <input
                            type="checkbox"
                            checked={marcados.has(a.id_arquivo)}
                            onChange={() => alternar(a.id_arquivo)}
                            aria-label={`Selecionar ${a.nome_original}`}
                          />
                        ) : (
                          /* Sem caixa, e o título diz por quê: a ausência
                             calada faria parecer defeito da tela. */
                          <span
                            className="col-meta"
                            title={
                              a.status === 'confirmado'
                                ? 'Já confirmado.'
                                : a.extracao_aprovada
                                  ? 'Ainda não está aguardando revisão.'
                                  : 'Não passou nas conferências aritméticas — só um administrador pode avaliar.'
                            }
                          >
                            —
                          </span>
                        )}
                      </td>
                    )}
                    <td className="col-principal">{a.operadora}</td>
                    <td className="col-codigo">{a.nome_original}</td>
                    <td className="col-meta">{a.competencia || '—'}</td>
                    <td className="col-qtd">{numero(a.qtd_guias)}</td>
                    <td className="col-valor">{brl(a.total_extraido)}</td>
                    <td className="col-qtd">
                      {/* Impostos retidos + auditoria: não é erro de conciliação. */}
                      {a.diferenca_bruto_liquido ? brl(a.diferenca_bruto_liquido) : '—'}
                    </td>
                    <td>
                      {conf.length === 0 ? (
                        <span className="col-meta">—</span>
                      ) : falhas === 0 ? (
                        <span className="ff-badge ff-badge--confirmado">
                          {conf.length} de {conf.length}
                        </span>
                      ) : (
                        <span className="ff-badge ff-badge--reprovado">
                          {falhas} falha{falhas === 1 ? '' : 's'}
                        </span>
                      )}
                    </td>
                    <td>
                      <StatusArquivoBadge status={a.status} />
                    </td>
                    <td className="col-data">{formatDateTime(a.created_at)}</td>
                    {isAdmin && (
                      <td className="col-data" style={{ textAlign: 'right' }}>
                        {/* `stopPropagation` porque a linha inteira navega
                            para o detalhe — sem isso, clicar em Excluir
                            abriria o arquivo por baixo do diálogo. */}
                        <Botao
                          variante="secundario"
                          pequeno
                          onClick={(e) => {
                            e.stopPropagation();
                            setAExcluir(a);
                          }}
                          title="Apaga o recebimento e tudo que foi apurado a partir dele. Os vínculos guia → protocolo já resolvidos são preservados."
                        >
                          Excluir
                        </Botao>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </Tabela>
        )}
      </Painel>

      {aConfirmar && (
        <DialogoConfirmacao
          arquivos={aConfirmar}
          aoFechar={() => setAConfirmar(null)}
          aoConfirmar={aplicarResultado}
        />
      )}

      {aExcluir && (
        <ConfirmarExclusaoArquivo
          arquivo={aExcluir}
          aoFechar={() => setAExcluir(null)}
          aoExcluir={(msg) => {
            setAExcluir(null);
            setAviso(msg);
            carregar();
          }}
        />
      )}
    </>
  );
}
